/**
 * Event-driven autosave for account-settings controls (Voice page).
 *
 * `save(patch)` writes ONLY the given fields via updateAccountProfile and
 * tracks an aggregate status for the page's indicator:
 *   idle → nothing in flight
 *   pending → ≥1 write in flight ("Saving…")
 *   saved → last write landed, decays to idle after ~2s ("✓ Saved")
 *   error → a write failed, sticky until the next save attempt
 *
 * Writes must originate from user-event handlers only (onChange/onBlur/
 * debounced gesture commits) — never from effects observing state. That
 * rule is what makes the old auto-save write-loop (#122) impossible.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { updateAccountPhrases, updateAccountProfile } from '@/lib/firestore';

export type AccountPatch = Parameters<typeof updateAccountProfile>[1];
export type PhrasesPatch = Parameters<typeof updateAccountPhrases>[1];

export type AutosaveStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'saved'; at: number }
  | { kind: 'error'; message: string };

const SAVED_DECAY_MS = 2000;

/** Firestore's raw errors read as jargon to caregivers. A rules rejection
 * surfaces as "Missing or insufficient permissions" even when the real
 * problem is a value the rules didn't accept (e.g. an over-long phrase). */
function friendlySaveError(e: unknown): string {
  const raw = e instanceof Error ? e.message : '';
  if (/insufficient permissions|permission-denied/i.test(raw)) {
    return "that change wasn't accepted. Try shortening it, or reload and try again.";
  }
  return raw || 'Save failed.';
}

export function useAccountAutosave(
  aid: string | null,
  enabled: boolean,
): {
  save: (patch: AccountPatch) => Promise<void>;
  savePhrases: (changes: PhrasesPatch) => Promise<void>;
  status: AutosaveStatus;
} {
  const [status, setStatus] = useState<AutosaveStatus>({ kind: 'idle' });
  const inFlight = useRef(0);

  // Shared status tracking around any account write.
  const run = useCallback(
    async (op: () => Promise<void>): Promise<void> => {
      // Non-admins get disabled controls; this is the defensive layer
      // behind them (rules would reject the write anyway).
      if (!aid || !enabled) return;
      inFlight.current += 1;
      setStatus({ kind: 'pending' });
      try {
        await op();
        inFlight.current -= 1;
        // Latch errors: if an OVERLAPPING write failed while this one was in
        // flight, its control has silently reverted — a later success must
        // not paper over that with "✓ Saved". The error stays until the next
        // save attempt (which sets pending above).
        if (inFlight.current === 0) {
          setStatus((s) => (s.kind === 'error' ? s : { kind: 'saved', at: Date.now() }));
        }
      } catch (e) {
        inFlight.current -= 1;
        setStatus({ kind: 'error', message: friendlySaveError(e) });
        throw e; // let per-field callers revert their local buffer
      }
    },
    [aid, enabled],
  );

  const save = useCallback(
    (patch: AccountPatch) => run(() => updateAccountProfile(aid ?? '', patch)),
    [run, aid],
  );
  const savePhrases = useCallback(
    (changes: PhrasesPatch) => run(() => updateAccountPhrases(aid ?? '', changes)),
    [run, aid],
  );

  // Decay "Saved" → idle.
  useEffect(() => {
    if (status.kind !== 'saved') return;
    const t = setTimeout(() => setStatus({ kind: 'idle' }), SAVED_DECAY_MS);
    return () => clearTimeout(t);
  }, [status]);

  return { save, savePhrases, status };
}
